import { pick, startCase } from "lodash";

import axiosInstance from "../axiosInstance";

type UI =
  | "numeric"
  | "select-dropdown"
  | "datetime"
  | "input"
  | "input-multiline"
  | "map"
  | "link"
  | "boolean";

export const columnWidthByUiType: Record<UI, number> = {
  datetime: 180,
  "input-multiline": 300,
  input: 150,
  numeric: 80,
  "select-dropdown": 120,
  map: 80,
  link: 60,
  boolean: 80,
};

export type FieldType =
  | "integer"
  | "string"
  | "date"
  | "timestamp"
  | "text"
  | "geometry.Point"
  | "calculated"
  | "boolean"
  | "float"
  | "decimal";

export interface FieldOption {
  label: string;
  value: string;
  color: string;
}

export interface FieldConfiguration {
  filterable: boolean;
  sortable: boolean;
  width: number;
  highlighted?: boolean;
  greenOnFilled?: boolean;
  searchSuggestion?: string;
  hideInput?: boolean;
  hiddenColumn?: boolean;
  userHidden?: boolean;
  searchType?: "exact" | "contains"; // New option for search behavior
  isLink?: boolean;
  availabilityPicker?: boolean;
}

export interface Field {
  name: string;
  type: FieldType;
  ui: UI;
  label: string;
  group: string;
  options?: FieldOption[];
  configuration: FieldConfiguration;
  sort: number;
  defaultValue?: any;
}

export const statusColors: Record<string, string> = {
  error: "red",
  draft: "orange",
  archived: "lightgrey",
  published: "green",
  test: "yellow",
};

const getConfiguration = (field: any): FieldConfiguration => {
  const afTranslation = field.meta.translations?.find(
    (t: any) => t?.language === "af-ZA"
  );
  if (afTranslation?.translation) {
    const configuration = JSON.parse(afTranslation.translation);

    return {
      ...configuration,
      width:
        configuration.width || columnWidthByUiType[field.meta.interface as UI],
      searchType: configuration.searchType || "contains", // Default to contains if not specified
    };
  }

  return {
    filterable: true,
    sortable: true,
    width: columnWidthByUiType[field.meta.interface as UI],
    searchType: "contains" as const, // Default to contains search for backward compatibility
  };
};

export const getCollectionFields = async (
  collectionsId: string
): Promise<Field[]> => {
  const response = await axiosInstance.get(
    `https://melcarnedirectus2.digipulse.ch/fields/${collectionsId}`
  );
  const fieldsWithSchema = response.data.data.filter(
    (field: any) =>
      !!field.schema &&
      field.schema.schema === "public" &&
      field.meta.hidden === false
  );

  const fieldsSpecification = fieldsWithSchema.map((field: any) => {
    return {
      name: field.field,
      type: field.type,
      ui: field.meta.interface,
      // to be updated with the translations
      label:
        field.field === "position_sur_map"
          ? "Liens utiles"
          : (() => {
              const frenchTranslation = field.meta.translations?.find(
                (t: any) => t?.language === "fr-FR"
              );
              return frenchTranslation?.translation || startCase(field.meta.field.replaceAll("_", " "));
            })(),
      group: field.meta.group === null ? "" : field.meta.group,
      options: (field.meta?.options?.choices || []).map((choice: any) => ({
        value: choice.value,
        label: choice.text,
        color: choice.color,
      })),
      configuration: getConfiguration(field),
      sort: field.meta.sort,
      defaultValue: field.schema?.default_value,
    };
  });

  return fieldsSpecification;
};

export type GroupedFields = Record<string, Field[]>;

export const getCollectionDefaultValues = async (
  collectionId: string
): Promise<Record<string, any>> => {
  const fields = await getCollectionFields(collectionId);
  const defaultValues: Record<string, any> = {};
  
  fields.forEach(field => {
    if (field.defaultValue !== undefined && field.defaultValue !== null) {
      defaultValues[field.name] = field.defaultValue;
    }
  });
  
  return defaultValues;
};

const inputFields = [
  "SurfaceBrutHab",
  "Balcon",
  "Terrasse",
  "emprise_au_sol",
  "Niveaux_HS",
  "Annexe_Prix",
  "Prix",
  "SousType",
  "status",
  "Terrain",
  "status_overriden",
];

export const calculatedFields: Record<
  string,
  (field: string, editedItem: any) => Promise<any>
> = {
  biens_off_market: async (field: string, editedItem: any) => {
    return {};
  },
  recherches: async (field: string, editedItem: any) => {
    return {};
  },
  sms_challenges: async(field: string, editedItem: any) => {
    return {};
  },
  transactions_fao: async (field: string, editedItem: any) => {
    if (inputFields.includes(field)) {
      const response = await axiosInstance.post(
        "https://melcarnebackend.digipulse.ch/calculate_svb",
        pick(editedItem, inputFields)
      );

      if (response.data["error"]) {
        console.error("Error during calculation", response.data["error"]);
      }

      return response.data;
    }

    return {};
  },
};
